import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current > 12) { setStatus("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-32 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 size={48} className="mx-auto text-[#B89C65] animate-spin" />
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-6">Confirmando pagamento…</h1>
          <p className="text-[#1A1A1A]/60 mt-3">Aguarde um instante.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="payment-success" className="fade-up">
          <CheckCircle2 size={56} strokeWidth={1.2} className="mx-auto text-[#1A1A1A]" />
          <h1 className="font-serif text-5xl text-[#1A1A1A] mt-6">Pedido confirmado!</h1>
          <p className="text-[#1A1A1A]/60 mt-4 leading-relaxed">
            Obrigado pela sua compra. Enviaremos as atualizações do envio em breve.
          </p>
          <div className="flex gap-4 justify-center mt-10">
            <Link to="/conta" data-testid="view-orders-btn" className="bg-[#1A1A1A] text-[#F8F7F4] px-8 py-4 overline hover:bg-[#B89C65] transition-colors">Meus pedidos</Link>
            <Link to="/loja" className="border border-[#1A1A1A]/30 px-8 py-4 overline hover:border-[#B89C65] hover:text-[#B89C65] transition-colors">Continuar comprando</Link>
          </div>
        </div>
      )}
      {status === "failed" && (
        <div data-testid="payment-failed">
          <XCircle size={56} strokeWidth={1.2} className="mx-auto text-[#B89C65]" />
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-6">Não foi possível confirmar</h1>
          <p className="text-[#1A1A1A]/60 mt-3">Se o valor foi debitado, entre em contato conosco.</p>
          <Link to="/carrinho" className="inline-block bg-[#1A1A1A] text-[#F8F7F4] px-8 py-4 mt-8 overline hover:bg-[#B89C65] transition-colors">Voltar à sacola</Link>
        </div>
      )}
    </div>
  );
}
